import { useEffect, useRef, useState } from "react";
import { Building2, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export function CompanyLookup({ value, onChange, onSelect }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const skipNext = useRef(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const term = value.trim();
    if (skipNext.current) { skipNext.current = false; return; }
    if (term.length < 2) { setItems([]); setOpen(false); setError(""); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true); setError("");
      try {
        const res = await api.get("/companies/search", { params: { q: term }, signal: controller.signal });
        setItems(res.data.items);
        setOpen(true);
      } catch (e) {
        if (e.name !== "CanceledError") { setError(e.response?.data?.detail || "Lookup unavailable"); setOpen(true); }
      } finally { setLoading(false); }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [value]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (c) => {
    skipNext.current = true;
    onSelect(c);
    setOpen(false);
    setItems([]);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        data-testid="contact-business"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => items.length > 0 && setOpen(true)}
        className="field w-full pr-10"
        placeholder="Start typing your company name"
        autoComplete="off"
      />
      {loading && <Loader2 className="w-4 h-4 text-lime animate-spin absolute right-4 top-1/2 -translate-y-1/2" />}
      {open && (
        <div data-testid="company-lookup-results" className="absolute z-30 left-0 right-0 mt-1 bg-[#111] border border-[#808080]/40 max-h-72 overflow-y-auto shadow-2xl">
          {error ? (
            <p className="p-4 text-sm text-grey">{error}</p>
          ) : items.length === 0 ? (
            <p className="p-4 text-sm text-grey">No companies found. You can still type your details manually.</p>
          ) : items.map((c) => (
            <button
              type="button"
              key={c.company_number}
              data-testid={`company-option-${c.company_number}`}
              onClick={() => pick(c)}
              className="w-full text-left px-4 py-3 border-b border-[#808080]/10 last:border-0 hover:bg-lime/10 transition-colors"
            >
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-lime mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{c.title}</p>
                  <p className="text-xs text-grey truncate">
                    {c.company_number} · <span className={c.company_status === "active" ? "text-lime" : ""}>{c.company_status}</span>
                    {c.address_snippet && ` · ${c.address_snippet}`}
                  </p>
                </div>
              </div>
            </button>
          ))}
          <p className="px-4 py-2 text-[10px] uppercase tracking-wide text-grey/60 border-t border-[#808080]/10">Powered by Companies House</p>
        </div>
      )}
    </div>
  );
}
